import React, { useState, useEffect } from 'react';
import {
  Users,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Download,
  Mail,
  Phone,
  Building,
  Calendar,
  RefreshCw
} from 'lucide-react';
import { leadsApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { useModal } from '../ui/ModalProvider';

const LeadManagement = () => {
  const { showConfirm } = useModal();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadLeads();
  }, []);

  useEffect(() => {
    filterLeads();
  }, [leads, searchTerm, statusFilter, sourceFilter]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      const response = await leadsApi.getLeads();
      setLeads(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load leads",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filterLeads = () => {
    let filtered = leads;

    if (searchTerm) {
      filtered = filtered.filter(lead =>
        lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead.company && lead.company.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(lead => lead.status === statusFilter);
    }

    if (sourceFilter) {
      filtered = filtered.filter(lead => lead.source === sourceFilter);
    }

    setFilteredLeads(filtered);
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      setUpdating(true);
      await leadsApi.updateLead(leadId, { status: newStatus });
      
      setLeads(prev => prev.map(lead =>
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      ));
      
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead(prev => ({ ...prev, status: newStatus }));
      }
      
      toast({
        title: "Success",
        description: `Lead status updated to ${newStatus}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update lead status",
        variant: "destructive"
      });
    } finally {
      setUpdating(false);
    }
  };

  const deleteLead = (leadId) => {
    showConfirm('Are you sure you want to delete this lead?', async () => {
      try {
        await leadsApi.deleteLead(leadId);
        setLeads(prev => prev.filter(lead => lead.id !== leadId));
        setShowModal(false);
        
        toast({
          title: "Success",
          description: "Lead deleted successfully",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete lead",
          variant: "destructive"
        });
      }
    });
  };

  const exportLeads = () => {
    const csvData = filteredLeads.map(lead => ({
      Name: lead.name,
      Email: lead.email,
      Company: lead.company || '',
      Phone: lead.phone || '',
      Service: lead.service || '',
      Budget: lead.budget || '',
      Status: lead.status,
      Source: lead.source,
      'Created At': new Date(lead.createdAt).toLocaleDateString(),
      Message: lead.message || ''
    }));

    const csvString = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).map(val => `"${val}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'contacted': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'qualified': return 'bg-green-100 text-green-800 border-green-200';
      case 'closed': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'contact_form': return <Mail size={16} className="text-blue-400" />;
      case 'newsletter': return <Mail size={16} className="text-green-400" />;
      case 'resource_download': return <Download size={16} className="text-orange-400" />;
      default: return <Mail size={16} className="text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto mb-4 border-4 border-gray-600 border-t-silver rounded-full animate-spin"></div>
          <p className="text-gray-400">Loading leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-silver mb-2">Lead Management</h1>
          <p className="text-gray-400">Manage and track your sales leads</p>
        </div>
        <div className="flex gap-3 mt-4 sm:mt-0">
          <button
            onClick={loadLeads}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-silver rounded-lg hover:bg-gray-700 transition-colors duration-300"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            onClick={exportLeads}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-300"
          >
            <Download size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-morphism rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-black/50 border border-gray-600 rounded-lg focus:border-silver focus:outline-none text-silver"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-black/50 border border-gray-600 rounded-lg focus:border-silver focus:outline-none text-silver"
          >
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="qualified">Qualified</option>
            <option value="closed">Closed</option>
          </select>

          {/* Source Filter */}
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-4 py-2 bg-black/50 border border-gray-600 rounded-lg focus:border-silver focus:outline-none text-silver"
          >
            <option value="">All Sources</option>
            <option value="contact_form">Contact Form</option>
            <option value="newsletter">Newsletter</option>
            <option value="resource_download">Resource Download</option>
          </select>

          {/* Results Count */}
          <div className="flex items-center text-gray-400">
            <Filter className="mr-2" size={16} />
            {filteredLeads.length} of {leads.length} leads
          </div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="glass-morphism rounded-xl p-6">
        {filteredLeads.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 font-medium text-gray-300">Contact</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-300">Company</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-300">Service</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-300">Source</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-300">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-300">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => (
                  <tr key={lead.id} className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors duration-300">
                    <td className="py-3 px-4">
                      <div>
                        <div className="text-silver font-medium">{lead.name}</div>
                        <div className="text-gray-400 text-sm flex items-center gap-1">
                          <Mail size={12} />
                          {lead.email}
                        </div>
                        {lead.phone && (
                          <div className="text-gray-400 text-sm flex items-center gap-1">
                            <Phone size={12} />
                            {lead.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1 text-gray-300">
                        <Building size={14} />
                        {lead.company || 'N/A'}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-300 capitalize">
                      {lead.service || 'General'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {getSourceIcon(lead.source)}
                        <span className="text-gray-300 text-sm capitalize">
                          {lead.source.replace('_', ' ')}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <select
                        value={lead.status}
                        onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                        disabled={updating}
                        className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(lead.status)} cursor-pointer`}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="qualified">Qualified</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                    <td className="py-3 px-4 text-gray-400 text-sm">
                      <div className="flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(lead.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedLead(lead);
                            setShowModal(true);
                          }}
                          className="p-1 text-blue-400 hover:text-blue-300 transition-colors duration-300"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => deleteLead(lead.id)}
                          className="p-1 text-red-400 hover:text-red-300 transition-colors duration-300"
                          title="Delete Lead"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Users size={48} className="mx-auto text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-silver mb-2">No leads found</h3>
            <p className="text-gray-400">
              {searchTerm || statusFilter || sourceFilter 
                ? 'Try adjusting your filters or search term.'
                : 'Leads will appear here when visitors submit the contact form.'}
            </p>
          </div>
        )}
      </div>

      {/* Lead Detail Modal */}
      {showModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-silver">Lead Details</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-silver transition-colors duration-300"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-semibold text-silver mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Name</label>
                    <p className="text-silver">{selectedLead.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Email</label>
                    <p className="text-silver">{selectedLead.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Company</label>
                    <p className="text-silver">{selectedLead.company || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Phone</label>
                    <p className="text-silver">{selectedLead.phone || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Lead Details */}
              <div>
                <h3 className="text-lg font-semibold text-silver mb-4">Lead Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Service Interest</label>
                    <p className="text-silver capitalize">{selectedLead.service || 'General'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Budget Range</label>
                    <p className="text-silver">{selectedLead.budget || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Source</label>
                    <p className="text-silver capitalize">{selectedLead.source.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Status</label>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedLead.status)}`}>
                      {selectedLead.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Message */}
              {selectedLead.message && (
                <div>
                  <h3 className="text-lg font-semibold text-silver mb-4">Message</h3>
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <p className="text-gray-300 leading-relaxed">{selectedLead.message}</p>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div>
                <h3 className="text-lg font-semibold text-silver mb-4">Timeline</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Created:</span>
                    <span className="text-silver">{new Date(selectedLead.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Last Updated:</span>
                    <span className="text-silver">{new Date(selectedLead.updatedAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 bg-gray-800 text-silver rounded-lg hover:bg-gray-700 transition-colors duration-300"
              >
                Close
              </button>
              <button
                onClick={() => deleteLead(selectedLead.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-300"
              >
                Delete Lead
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadManagement;